import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  CtaBand,
  DirectAnswer,
  Eyebrow,
  Lead,
  PrimaryLink,
  TextLink,
} from "@/components/site/primitives";
import { SITE } from "@/lib/site-content";
import { ogImageMeta, ogImageUrl } from "@/lib/og";
import { articleSchema, jsonLdScript } from "@/lib/schema";
import { contactFunnel } from "@/lib/analytics";
import { RelatedLinks } from "@/components/site/RelatedLinks";

const PATH = "/when-to-hire-your-first-cto";
const CTA_SOURCE_HERO = "first_cto_hero";
const CTA_SOURCE_BAND = "first_cto_footer";
const REVIEWED = "5 August 2026";

const title = "When to Hire Your First CTO: The Signals That Matter";
const description =
  "The signals that say you need a first CTO, the ones that only look like it, and the part-time options that buy you time before the hire.";

const ANSWER =
  "Hire your first full-time CTO when technical decisions must be made daily, a technical team needs line management, and the cost of a wrong architecture or hiring decision now exceeds an executive salary. Before that point, part-time senior judgement usually closes the gap at lower cost and lower risk.";

const SIGNALS = [
  {
    title: "Technical decisions are queuing behind you",
    body: "If architecture, security and supplier decisions wait for a founder who is also selling, raising and hiring, the queue itself is the cost. Slow decisions compound quietly until a rebuild is the only option left.",
  },
  {
    title: "There is a technical team with nobody running it",
    body: "Engineers need someone who can set standards, review work and hire the next person. A team of three or more without technical leadership will drift towards whatever each individual thinks is right.",
  },
  {
    title: "You are paying an outsourced team on trust",
    body: "Someone independent has to read what a development agency promised against what it shipped. If nobody in the business can do that, you are approving invoices you cannot verify.",
  },
  {
    title: "Investors are asking questions you cannot answer",
    body: "Technical due diligence asks about architecture, security, licensing, key-person risk and the credibility of the roadmap. Assembling those answers during a raise is far more expensive than having them ready.",
  },
];

const NOT_YET = [
  {
    title: "You need code written, not decisions made",
    body: "Wanting more shipped is a capacity problem. A CTO is a judgement hire. Hiring an executive to solve a capacity problem gets you an expensive senior engineer who is bored within a quarter.",
  },
  {
    title: "The product direction is still moving weekly",
    body: "Before product-market fit, most technical decisions are cheap to reverse. Locking in an executive with strong architectural opinions too early can freeze choices you still need to keep open.",
  },
  {
    title: "You want the title on the pitch deck",
    body: "Investors test the substance behind the title. A credible technical story, verifiable roadmap and named advisor stand up better than a rushed appointment that does not survive diligence.",
  },
  {
    title: "You cannot describe the first six months",
    body: "If you cannot write down what a CTO would own in their first two quarters and how you would judge it, the role is not defined enough to hire against. Define the work first.",
  },
];

const FAQ = [
  {
    q: "At what stage should a startup hire its first CTO?",
    a: "There is no headcount or funding threshold that settles it. The practical test is whether technical decisions now need making daily, whether a technical team needs line management, and whether the cost of getting architecture, security or hiring wrong exceeds the cost of the salary.",
  },
  {
    q: "Do I need a CTO or a lead engineer?",
    a: "If the gap is shipping more code to a known plan, you need a lead engineer or another developer. If the gap is deciding what the plan should be, holding suppliers to account and answering to investors, that is a CTO-level responsibility.",
  },
  {
    q: "Can a fractional CTO delay the full-time hire?",
    a: "Usually, and often it should. Part-time senior judgement covers architecture, delivery assurance, supplier oversight and hiring without the salary, equity and search cost, and a fractional CTO frequently runs the recruitment for the permanent hire that eventually replaces them.",
  },
  {
    q: "What should a first CTO own in their first six months?",
    a: "A written view of where technology, team, spend and risk stand today; a technology plan that follows the business plan, with sequence and cost named; delivery discipline that makes commitments verifiable; and a hiring plan the business can afford.",
  },
  {
    q: "Should a co-founder become the CTO by default?",
    a: "Only if the role, as it now exists, matches what they can do and want to do. The technical co-founder who built the first version is not automatically the right person to run a department, and treating that as automatic damages both the business and the relationship.",
  },
];

export const Route = createFileRoute("/when-to-hire-your-first-cto")({
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta("when-to-hire-your-first-cto", "Guide: when to hire your first CTO"),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      jsonLdScript(
        articleSchema({
          path: PATH,
          headline: title,
          description,
          datePublished: "2026-08-05",
          dateModified: "2026-08-05",
          image: ogImageUrl("when-to-hire-your-first-cto"),
        }),
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
            {
              "@type": "ListItem",
              position: 2,
              name: "Fractional CTO",
              item: `${SITE.url}/what-i-do`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "When to hire your first CTO",
              item: `${SITE.url}${PATH}`,
            },
          ],
        }),
      },
    ],
  }),
  component: FirstCtoPage,
});

function FirstCtoPage() {
  return (
    <div>
      <section
        aria-labelledby="fct-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Eyebrow>Guide</Eyebrow>
          <h1
            id="fct-hero-heading"
            className="mt-4 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            When to hire your first CTO
          </h1>
          <DirectAnswer className="mt-6">{ANSWER}</DirectAnswer>
          <Lead className="mt-6">
            The signals that say it is time, the ones that only look like it, and what to do in the
            months before you can justify the salary.
          </Lead>
          <div className="mt-8">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Arrange a conversation
            </PrimaryLink>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fct-signals-heading">
        <Container>
          <Eyebrow>Signals</Eyebrow>
          <h2
            id="fct-signals-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            When should you hire your first CTO? Four signals
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {SIGNALS.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fct-notyet-heading">
        <Container>
          <Eyebrow>Not yet</Eyebrow>
          <h2
            id="fct-notyet-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Four reasons that look like signals but are not
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {NOT_YET.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fct-between-heading">
        <Container>
          <h2
            id="fct-between-heading"
            className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            What to do before you can justify the salary
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body">
            <p>
              Most companies reach the judgement gap well before they reach the payroll for an
              executive. The workable answer in between is part-time senior involvement: an audit
              that states plainly where technology, team, spend and risk stand, then a cadence that
              keeps decisions moving and holds suppliers to what they promised.
            </p>
            <p>
              Done properly, that arrangement is temporary by design. It should leave written
              decisions, a plan the board can verify, and stronger technical leads inside the
              business, and it often runs the hiring process for the permanent CTO who replaces it.
            </p>
            <p>
              If what you actually have is a vacant full-time seat rather than a judgement gap, the
              right answer is an interim, and the difference is set out in the{" "}
              <TextLink to="/interim-cto-vs-fractional-cto">
                interim versus fractional comparison
              </TextLink>
              .
            </p>
          </div>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fct-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="fct-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Common questions
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
          <RelatedLinks
            className="mt-7"
            paths={[
              "/fractional-cto-vs-full-time-vs-agency",
              "/case-studies",
              "/fractional-cto-uk",
            ]}
          />
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fct-sources-heading">
        <Container>
          <h2 id="fct-sources-heading" className="text-xl font-bold tracking-[-0.02em]">
            Sources and review
          </h2>
          <p className="mt-4 max-w-[70ch] text-[15px] leading-relaxed text-body">
            This guide quotes no benchmark salaries or stage thresholds, because the published
            figures for first-CTO hires are marketing material rather than survey data. Everything
            above is drawn from twenty years of building and running technology organisations as a
            CTO, a COO and a consultancy founder, and is stated as experience.
          </p>
          <p className="mt-4 text-[15px] text-muted-foreground">
            Last reviewed: <time dateTime="2026-08-05">{REVIEWED}</time>
          </p>
        </Container>
      </section>

      <CtaBand
        heading="Working out whether it is time?"
        body="Describe what is queuing and who currently decides it. I will tell you whether this is a hire, a part-time arrangement, or something you can leave alone for now."
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
